import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabasePublic, supabaseSchema } from "./supabaseClient";
import { parseAudioPeriodicBackupSettings } from "../pages/AudioRecorder/services/externalRecordingService";

const clampKeep = (n) =>
  Math.max(0, Math.min(100, Math.trunc(Number(n)) || 0));
const clampInterval = (n) =>
  Math.max(5, Math.min(600, Math.trunc(Number(n)) || 20));

function activeUserSchemaName() {
  const raw = (supabaseSchema || "public").trim();
  return raw.includes(",") ? raw.split(",")[0].trim() : raw;
}

const AudioPeriodicBackupAdminSettings = ({ onClose, isOpen }) => {
  const toast = useToast();
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [keepCount, setKeepCount] = useState(2);
  const [intervalSeconds, setIntervalSeconds] = useState(20);
  const [loadingSchemas, setLoadingSchemas] = useState(true);
  const [loadingRow, setLoadingRow] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSchemaNames = useCallback(async () => {
    setLoadingSchemas(true);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("schema_name")
        .order("schema_name");
      if (error) throw error;
      const names = (data || []).map((r) => r.schema_name).filter(Boolean);
      setSchemas(names);
    } catch (err) {
      console.error("Audio backup admin: schema list failed", err);
      toast({
        title: "Could not load schemas",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSchemas(false);
    }
  }, [toast]);

  const loadRow = useCallback(async () => {
    if (!selectedSchema) return;
    setLoadingRow(true);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("audio_periodic_backup")
        .eq("schema_name", selectedSchema)
        .maybeSingle();
      if (error) throw error;
      const parsed = parseAudioPeriodicBackupSettings(data?.audio_periodic_backup);
      setKeepCount(parsed.keepCount);
      setIntervalSeconds(parsed.intervalSeconds);
    } catch (err) {
      console.error("Audio backup admin: load failed", err);
      toast({
        title: "Could not load settings",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingRow(false);
    }
  }, [selectedSchema, toast]);

  useEffect(() => {
    loadSchemaNames();
  }, [loadSchemaNames]);

  useEffect(() => {
    if (!isOpen || schemas.length === 0) return;
    const preferred = activeUserSchemaName();
    setSelectedSchema(
      schemas.includes(preferred) ? preferred : schemas[0] || "",
    );
  }, [isOpen, schemas]);

  useEffect(() => {
    if (selectedSchema) loadRow();
  }, [selectedSchema, loadRow]);

  const handleSave = async () => {
    if (!selectedSchema) return;
    const payload = {
      keep_count: clampKeep(keepCount),
      interval_seconds: clampInterval(intervalSeconds),
    };
    setSaving(true);
    try {
      const { error: updErr } = await supabasePublic()
        .from("schema_settings")
        .update({ audio_periodic_backup: payload })
        .eq("schema_name", selectedSchema);
      if (updErr) throw updErr;

      toast({
        title: "Saved",
        description: `Audio backup settings updated for “${selectedSchema}”.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      if (onClose) onClose();
    } catch (err) {
      console.error("Audio backup admin: save failed", err);
      toast({
        title: "Save failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loadingSchemas) {
    return (
      <Box py={6} textAlign="center">
        <Spinner />
        <Text mt={2} color="gray.500">
          Loading schemas…
        </Text>
      </Box>
    );
  }

  if (!schemas.length) {
    return (
      <Text color="gray.600">
        No rows in <code>schema_settings</code>. Add schemas or run your DB
        migration first.
      </Text>
    );
  }

  return (
    <VStack spacing={5} align="stretch" py={1}>
      <FormControl>
        <FormLabel>Schema</FormLabel>
        <Select
          value={selectedSchema}
          onChange={(e) => setSelectedSchema(e.target.value)}
          isDisabled={loadingRow}
        >
          {schemas.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        <FormHelperText>
          Settings are stored on <code>public.schema_settings</code> for this{" "}
          <code>schema_name</code>.
        </FormHelperText>
      </FormControl>

      {loadingRow ? (
        <Box textAlign="center" py={4}>
          <Spinner size="sm" />
        </Box>
      ) : (
        <>
          <FormControl>
            <FormLabel>Playable backups to keep after save</FormLabel>
            <NumberInput
              min={0}
              max={100}
              value={keepCount}
              onChange={(_, v) => setKeepCount(Number.isFinite(v) ? v : 0)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              After a successful recording save, at most this many{" "}
              <strong>valid</strong> periodic backup files stay in storage (0–100).
              Unplayable backups in the newest batch may be kept until enough valid
              ones exist.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Backup interval while recording (seconds)</FormLabel>
            <NumberInput
              min={5}
              max={600}
              value={intervalSeconds}
              onChange={(_, v) =>
                setIntervalSeconds(Number.isFinite(v) ? v : 20)
              }
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              How often the external recorder uploads a new audio backup while
              recording (5–600 seconds).
            </FormHelperText>
          </FormControl>
        </>
      )}

      <Button
        colorScheme="blue"
        onClick={handleSave}
        isLoading={saving}
        isDisabled={!selectedSchema || loadingRow}
      >
        Save
      </Button>
    </VStack>
  );
};

export default AudioPeriodicBackupAdminSettings;
